import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  getUsageSummary,
  getOrgUsage,
  exportUsageCsv,
  UsageSummaryResponse,
  OrgUsageSummary,
  OrgUsageDetailResponse,
  UsageBreakdownRow,
  UsageTimeseriesPoint,
  UsageTotals,
} from '../../services/Usage';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Download, Loader2, ArrowUpDown, ChevronRight, ArrowLeft, Info } from 'lucide-react';
import {
  CostOverTimeChart,
  assignFeatureColors,
  OTHER_COLOR,
} from './usage/CostOverTimeChart';
import { featureLabel, formatNumber, formatTokens } from './usage/usage-format';
import { SekRateProvider, formatSek, formatUsdTitle, useSek } from './usage/sek';
import DigestCard from './DigestCard';

// ---------------------------------------------------------------------------
// Date presets
// ---------------------------------------------------------------------------

type DatePreset = '7d' | '30d' | 'month' | '3m';

const getDateRange = (preset: DatePreset): { startDate: string; endDate: string } => {
  const now = new Date();
  const start = new Date(now);
  switch (preset) {
    case '7d':
      start.setDate(start.getDate() - 6);
      break;
    case '30d':
      start.setDate(start.getDate() - 29);
      break;
    case 'month':
      start.setDate(1);
      break;
    case '3m':
      start.setMonth(start.getMonth() - 3);
      break;
  }
  start.setHours(0, 0, 0, 0);
  return { startDate: start.toISOString(), endDate: now.toISOString() };
};

const presetLabels: Record<DatePreset, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  month: 'This month',
  '3m': 'Last 3 months',
};

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

const StatTile: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <Card className="p-4">
    <p className="text-sm text-muted-foreground">{label}</p>
    <p className="text-2xl font-bold mt-1 tabular-nums">{value}</p>
    {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
  </Card>
);

const Notices: React.FC<{ totals: UsageTotals }> = ({ totals }) => {
  const formatUsd = useSek();
  if (totals.estimatedCostUsd <= 0 && totals.unpricedCalls <= 0) return null;
  return (
    <div className="mb-6 flex gap-2 rounded-md border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
      <Info className="h-4 w-4 mt-0.5 shrink-0" />
      <div className="space-y-1">
        {totals.estimatedCostUsd > 0 && (
          <p>
            Includes <span className="text-foreground font-medium">{formatUsd(totals.estimatedCostUsd)}</span> of
            estimated QAgent cost from before per-call tracking, priced at Claude Haiku 4.5 rates.
          </p>
        )}
        {totals.unpricedCalls > 0 && (
          <p>
            {formatNumber(totals.unpricedCalls)} call{totals.unpricedCalls === 1 ? '' : 's'} used a model without a
            known price and {totals.unpricedCalls === 1 ? 'is' : 'are'} not included in cost.
          </p>
        )}
      </div>
    </div>
  );
};

/** Magnitude table with an inline share bar; colours match the chart legend. */
const BreakdownCard: React.FC<{
  title: string;
  rows: UsageBreakdownRow[];
  totalCost: number;
  label: (key: string) => string;
  color?: (key: string) => string;
}> = ({ title, rows, totalCost, label, color }) => {
  const formatUsd = useSek();
  const max = Math.max(0, ...rows.map((r) => r.costUsd));
  return (
    <Card className="p-4">
      <h2 className="font-semibold mb-3">{title}</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No usage for this period.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40%]">Name</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead className="text-right">Share</TableHead>
              <TableHead className="text-right">Calls</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {color && (
                      <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: color(row.key) }} />
                    )}
                    <span className="truncate" title={row.key}>{label(row.key)}</span>
                  </div>
                  <div className="mt-1 h-1.5 rounded-full bg-muted">
                    <div
                      className="h-1.5 rounded-full"
                      style={{
                        width: `${max > 0 ? (row.costUsd / max) * 100 : 0}%`,
                        backgroundColor: color ? color(row.key) : 'hsl(var(--foreground) / 0.45)',
                      }}
                    />
                  </div>
                </TableCell>
                <TableCell className="text-right tabular-nums" title={formatUsdTitle(row.costUsd)}>
                  {formatUsd(row.costUsd)}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {totalCost > 0 ? `${((row.costUsd / totalCost) * 100).toFixed(1)}%` : '–'}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">{formatNumber(row.calls)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
};

const Overview: React.FC<{
  totals: UsageTotals;
  timeseries: UsageTimeseriesPoint[];
  byFeature: UsageBreakdownRow[];
  byModel: UsageBreakdownRow[];
  startDate: string;
  endDate: string;
  scope: 'all' | 'org';
  /** Colours from the all-organizations view, so a feature keeps its colour when drilling in */
  globalColors?: Record<string, string>;
}> = ({ totals, timeseries, byFeature, byModel, startDate, endDate, scope, globalColors }) => {
  const formatUsd = useSek();
  const featuresByCost = useMemo(() => byFeature.map((f) => f.key), [byFeature]);
  const colors = useMemo(
    () => globalColors ?? assignFeatureColors(featuresByCost),
    [globalColors, featuresByCost],
  );

  return (
    <>
      <Notices totals={totals} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatTile
          label="AI cost"
          value={formatUsd(totals.costUsd)}
          hint={totals.estimatedCostUsd > 0 ? `incl. ${formatUsd(totals.estimatedCostUsd)} estimated` : undefined}
        />
        <StatTile label="LLM calls" value={formatNumber(totals.calls)} />
        <StatTile
          label="Tokens"
          value={formatTokens(totals.totalTokens)}
          hint={`${formatTokens(totals.inputTokens)} in · ${formatTokens(totals.outputTokens)} out`}
        />
        {scope === 'all' ? (
          <StatTile label="Active organizations" value={formatNumber(totals.activeOrgs)} hint={`${formatNumber(totals.activeUsers)} active users`} />
        ) : (
          <StatTile label="Active users" value={formatNumber(totals.activeUsers)} />
        )}
      </div>

      <Card className="p-4 mb-6">
        <h2 className="font-semibold mb-3">Cost over time</h2>
        {totals.calls === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">No usage for this period.</p>
        ) : (
          <CostOverTimeChart
            timeseries={timeseries}
            featuresByCost={featuresByCost}
            colors={colors}
            startDate={startDate}
            endDate={endDate}
          />
        )}
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
        <BreakdownCard
          title="By feature"
          rows={byFeature}
          totalCost={totals.costUsd}
          label={featureLabel}
          color={(key) => colors[key] ?? OTHER_COLOR}
        />
        <BreakdownCard title="By model" rows={byModel} totalCost={totals.costUsd} label={(k) => k} />
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

type OrgSortKey = 'organizationName' | 'costUsd' | 'totalTokens' | 'calls' | 'activeUsers';

const UsageDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usageData, setUsageData] = useState<UsageSummaryResponse | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<OrgUsageSummary | null>(null);
  const [orgDetail, setOrgDetail] = useState<OrgUsageDetailResponse | null>(null);
  const [orgDetailLoading, setOrgDetailLoading] = useState(false);
  const [datePreset, setDatePreset] = useState<DatePreset>('30d');
  const [exporting, setExporting] = useState(false);
  const [sortKey, setSortKey] = useState<OrgSortKey>('costUsd');
  const [sortAsc, setSortAsc] = useState(false);

  const { startDate, endDate } = useMemo(() => getDateRange(datePreset), [datePreset]);
  const sekPerUsd = (orgDetail ?? usageData)?.sekPerUsd ?? 0;
  const formatUsd = (usd: number) => formatSek(usd * sekPerUsd);
  const globalColors = useMemo(
    () => (usageData ? assignFeatureColors(usageData.byFeature.map((f) => f.key)) : undefined),
    [usageData],
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getUsageSummary(startDate, endDate);
      if (response.success && response.data) {
        setUsageData(response.data);
      } else {
        setError('Failed to load usage data');
      }
    } catch {
      setError('An error occurred while loading usage data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const loadOrg = useCallback(
    async (org: OrgUsageSummary) => {
      setOrgDetailLoading(true);
      try {
        const response = await getOrgUsage(org.organizationId, startDate, endDate);
        if (response.success && response.data) {
          setOrgDetail(response.data);
        } else {
          toast.error('Failed to load organization details');
        }
      } catch {
        toast.error('An error occurred while loading organization details');
      } finally {
        setOrgDetailLoading(false);
      }
    },
    [startDate, endDate],
  );

  // Keep the drill-down in sync when the date range changes
  useEffect(() => {
    if (selectedOrg) loadOrg(selectedOrg);
  }, [selectedOrg, loadOrg]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportUsageCsv(startDate, endDate);
      if (!response.success || !response.data) throw new Error();
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `queri-ai-usage-${startDate.slice(0, 10)}-to-${endDate.slice(0, 10)}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error('Failed to export usage CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (key: OrgSortKey) => {
    if (sortKey === key) setSortAsc((prev) => !prev);
    else {
      setSortKey(key);
      setSortAsc(key === 'organizationName');
    }
  };

  const sortedOrgs = useMemo(() => {
    if (!usageData) return [];
    return [...usageData.organizations].sort((a, b) => {
      const dir = sortAsc ? 1 : -1;
      if (sortKey === 'organizationName') return a.organizationName.localeCompare(b.organizationName) * dir;
      return (a[sortKey] - b[sortKey]) * dir;
    });
  }, [usageData, sortKey, sortAsc]);

  const SortableHeader: React.FC<{ label: string; field: OrgSortKey; align?: 'right' }> = ({ label, field, align }) => (
    <TableHead
      className="cursor-pointer select-none hover:bg-muted/50"
      onClick={() => handleSort(field)}
      aria-sort={sortKey === field ? (sortAsc ? 'ascending' : 'descending') : 'none'}
    >
      <div className={`flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        {label}
        <ArrowUpDown className={`h-3 w-3 ${sortKey === field ? 'text-foreground' : 'text-muted-foreground'}`} />
      </div>
    </TableHead>
  );

  const header = (title: React.ReactNode, back?: () => void) => (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
      <div className="flex items-center gap-3">
        {back && (
          <Button variant="ghost" size="sm" onClick={back} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            All organizations
          </Button>
        )}
        <h1 className="text-3xl font-bold">{title}</h1>
      </div>
      <div className="flex items-center gap-3">
        <Select value={datePreset} onValueChange={(v) => setDatePreset(v as DatePreset)}>
          <SelectTrigger className="w-[180px]" aria-label="Date range">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(presetLabels) as DatePreset[]).map((key) => (
              <SelectItem key={key} value={key}>
                {presetLabels[key]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {!back && (
          <Button variant="outline" onClick={handleExport} disabled={exporting}>
            {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Export CSV
          </Button>
        )}
      </div>
    </div>
  );

  if (loading && !usageData) {
    return (
      <div className="flex justify-center mt-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <p className="text-destructive">{error}</p>
        <Button className="mt-4" onClick={fetchData}>
          Retry
        </Button>
      </div>
    );
  }

  // --- Organization drill-down ------------------------------------------------
  if (selectedOrg) {
    return (
      <div className="p-8">
        {header(selectedOrg.organizationName, () => {
          setSelectedOrg(null);
          setOrgDetail(null);
        })}
        {orgDetailLoading && !orgDetail ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : orgDetail ? (
          <>
            <SekRateProvider rate={orgDetail.sekPerUsd}>
              <Overview {...orgDetail} scope="org" globalColors={globalColors} />
            </SekRateProvider>
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                    <TableHead className="text-right">Input tokens</TableHead>
                    <TableHead className="text-right">Output tokens</TableHead>
                    <TableHead className="text-right">Calls</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orgDetail.users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No user activity for this period.
                      </TableCell>
                    </TableRow>
                  ) : (
                    orgDetail.users.map((user) => {
                      const name = `${user.firstName} ${user.lastName}`.trim();
                      return (
                        <TableRow key={user.userId ?? 'system'}>
                          <TableCell>
                            {user.userId ? (
                              <>
                                <p className="font-medium">{name || user.email}</p>
                                {name && <p className="text-xs text-muted-foreground">{user.email}</p>}
                              </>
                            ) : (
                              <p className="text-muted-foreground">System / background</p>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums" title={formatUsdTitle(user.costUsd)}>
                            {formatUsd(user.costUsd)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{formatNumber(user.inputTokens)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatNumber(user.outputTokens)}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatNumber(user.calls)}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Card>
          </>
        ) : null}
      </div>
    );
  }

  // --- All organizations --------------------------------------------------------
  return (
    <div className="p-8">
      {header('AI Usage & Cost')}
      {usageData && (
        <>
          <SekRateProvider rate={usageData.sekPerUsd}>
            <Overview {...usageData} scope="all" />
          </SekRateProvider>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader label="Organization" field="organizationName" />
                  <SortableHeader label="Cost" field="costUsd" align="right" />
                  <SortableHeader label="Tokens" field="totalTokens" align="right" />
                  <SortableHeader label="Calls" field="calls" align="right" />
                  <SortableHeader label="Users" field="activeUsers" align="right" />
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedOrgs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No usage data for this period.
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedOrgs.map((org) => (
                    <TableRow
                      key={org.organizationId ?? 'unattributed'}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setSelectedOrg(org)}
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && setSelectedOrg(org)}
                    >
                      <TableCell className={`font-medium ${org.organizationId ? '' : 'text-muted-foreground italic'}`}>
                        {org.organizationName}
                      </TableCell>
                      <TableCell className="text-right tabular-nums" title={formatUsdTitle(org.costUsd)}>
                        {formatUsd(org.costUsd)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatTokens(org.totalTokens)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatNumber(org.calls)}</TableCell>
                      <TableCell className="text-right tabular-nums">{org.activeUsers}</TableCell>
                      <TableCell>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
      <DigestCard />
    </div>
  );
};

export default UsageDashboard;
